import dayjs from "dayjs";

import { formatMoney } from "../../shared/currency";
import { asPlatforms } from "../../shared/platforms";
import type { Campaign, CampaignType, Client, FilterRange, Platform, TimestampOrDate, User } from "../../shared/types";
import { timestampOrDateToDate } from "../../utils/dates";
import { isPitchReviewer } from "../../utils/pitching/permissions";

export type OpportunityListFilter = {
    campaignTypes: CampaignType[];
    platforms: Platform[];
    companySectors: NonNullable<Client["companySector"]>[];
    priceRange: FilterRange;
};

export const emptyOpportunityListFilter: OpportunityListFilter = {
    campaignTypes: [],
    platforms: [],
    companySectors: [],
    priceRange: { lower: null, upper: null },
};

/** Managers and admins see every index row. Everyone else is limited to campaigns they can review. */
export function opportunityVisibleTo(
    user: User | undefined,
    campaign: Pick<Campaign, "createdById" | "booker">,
): boolean {
    if (user?.roles?.manager || user?.roles?.admin) {
        return true;
    }
    return isPitchReviewer(user, campaign);
}

export function opportunityMatchesSearch(campaign: Pick<Campaign, "campaignName" | "booker">, search: string): boolean {
    const query = search.trim().toLowerCase();
    if (!query) {
        return true;
    }
    const name = campaign.campaignName?.toLowerCase() || "";
    const company = campaign.booker?.company?.toLowerCase() || "";
    return name.includes(query) || company.includes(query);
}

/** Type is any selected type. Platform is any selected platform. Sector is any selected client sector. Price is overlap with campaign min/max (missing bounds unbounded). Empty groups do not constrain. */
export function opportunityMatchesListFilter(
    campaign: Pick<Campaign, "campaignType" | "campaignPlatform" | "minPricePerCreator" | "maxPricePerCreator">,
    filter: OpportunityListFilter,
    companySector?: string,
): boolean {
    if (filter.campaignTypes.length > 0) {
        if (!campaign.campaignType || !filter.campaignTypes.includes(campaign.campaignType)) {
            return false;
        }
    }
    if (filter.platforms.length > 0) {
        const platforms = asPlatforms(campaign.campaignPlatform);
        if (!platforms.some((platform) => filter.platforms.includes(platform))) {
            return false;
        }
    }
    if (filter.companySectors.length > 0) {
        if (!companySector || !filter.companySectors.some((sector) => sector === companySector)) {
            return false;
        }
    }
    if (!campaignPriceOverlapsRange(campaign, filter.priceRange)) {
        return false;
    }
    return true;
}

export function opportunityFilterCount(filter: OpportunityListFilter): number {
    const priceActive = filter.priceRange.lower != null || filter.priceRange.upper != null ? 1 : 0;
    return filter.campaignTypes.length + filter.platforms.length + filter.companySectors.length + priceActive;
}

/** Open Pricing (both bounds missing) is unbounded and overlaps any price filter. Stored numbers, no FX. */
function campaignPriceOverlapsRange(
    campaign: Pick<Campaign, "minPricePerCreator" | "maxPricePerCreator">,
    range: FilterRange,
): boolean {
    if (range.lower == null && range.upper == null) {
        return true;
    }
    if (range.lower != null && campaign.maxPricePerCreator != null && campaign.maxPricePerCreator < range.lower) {
        return false;
    }
    if (range.upper != null && campaign.minPricePerCreator != null && campaign.minPricePerCreator > range.upper) {
        return false;
    }
    return true;
}

export function sortOpportunitiesByRecency<T extends Pick<Campaign, "pitchingOpenChangedAt">>(campaigns: readonly T[]): T[] {
    return [...campaigns].sort((a, b) => openedAtMs(b.pitchingOpenChangedAt) - openedAtMs(a.pitchingOpenChangedAt));
}

export function opportunityRecencyLabel(openedAt: TimestampOrDate | undefined, now = new Date()): string {
    if (!openedAt) {
        return "";
    }
    const date = timestampOrDateToDate(openedAt, false) as Date;
    const days = dayjs(now).startOf("day").diff(dayjs(date).startOf("day"), "day");
    if (days <= 0) {
        return "Today";
    }
    if (days === 1) {
        return "1 Day Ago";
    }
    return `${days} Days Ago`;
}

export function opportunityPriceLabel(
    campaign: Pick<Campaign, "minPricePerCreator" | "maxPricePerCreator" | "currency">,
): string {
    const currency = campaign.currency || "USD";
    const minLabel = formatMoney(campaign.minPricePerCreator, currency);
    const maxLabel = formatMoney(campaign.maxPricePerCreator, currency);
    if (minLabel && maxLabel) {
        return `${minLabel} - ${maxLabel}`;
    }
    if (minLabel) {
        return `From ${minLabel}`;
    }
    if (maxLabel) {
        return `Up to ${maxLabel}`;
    }
    return "Open Pricing";
}

export function pitchCountLabel(count: number | undefined): string {
    const n = count ?? 0;
    return `${n} ${n === 1 ? "Pitch" : "Pitches"}`;
}

function openedAtMs(openedAt: TimestampOrDate | undefined): number {
    if (!openedAt) {
        return 0;
    }
    return (timestampOrDateToDate(openedAt, false) as Date).getTime();
}
